import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WebhookIcon from "@mui/icons-material/Webhook";
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  type WebhookEndpoint,
} from "@/api/dashboard";
import EmptyState from "@/components/EmptyState";

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhooksError, setWebhooksError] = useState<string | null>(null);
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [newWebhookEventCompleted, setNewWebhookEventCompleted] =
    useState(true);
  const [newWebhookEventFailed, setNewWebhookEventFailed] = useState(true);
  const [addWebhookSaving, setAddWebhookSaving] = useState(false);
  const [newSecretAlert, setNewSecretAlert] = useState<string | null>(null);

  const loadWebhooks = useCallback(async () => {
    setWebhooksLoading(true);
    setWebhooksError(null);
    try {
      const data = await getWebhooks();
      setWebhooks(data);
    } catch {
      setWebhooksError("Failed to load webhooks");
    } finally {
      setWebhooksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWebhooks();
  }, [loadWebhooks]);

  const handleAddWebhook = async () => {
    if (!newWebhookUrl) return;
    const events = [
      newWebhookEventCompleted ? "render.completed" : null,
      newWebhookEventFailed ? "render.failed" : null,
    ]
      .filter(Boolean)
      .join(",");
    setAddWebhookSaving(true);
    try {
      const created = await createWebhook(newWebhookUrl, events || undefined);
      setNewSecretAlert(created.secret);
      setAddWebhookOpen(false);
      setNewWebhookUrl("");
      setNewWebhookEventCompleted(true);
      setNewWebhookEventFailed(true);
      await loadWebhooks();
    } catch {
      setWebhooksError("Failed to create webhook");
    } finally {
      setAddWebhookSaving(false);
    }
  };

  const handleToggleWebhook = async (id: string, enabled: boolean) => {
    try {
      const updated = await updateWebhook(id, { enabled });
      setWebhooks((prev) => prev.map((w) => (w.id === id ? updated : w)));
    } catch {
      setWebhooksError("Failed to update webhook");
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    try {
      await deleteWebhook(id);
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
    } catch {
      setWebhooksError("Failed to delete webhook");
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Webhooks
      </Typography>

      {newSecretAlert && (
        <Alert
          severity="success"
          onClose={() => setNewSecretAlert(null)}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Webhook created! Copy this secret — it won&apos;t be shown again:
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              fontFamily: "monospace",
              wordBreak: "break-all",
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {newSecretAlert}
            </Typography>
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(newSecretAlert)}
                aria-label="Copy webhook secret to clipboard"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={() => setAddWebhookOpen(true)}>
          Add Endpoint
        </Button>
      </Box>

      {webhooksLoading && <LinearProgress />}
      {webhooksError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {webhooksError}
        </Alert>
      )}

      {!webhooksLoading && !webhooksError && webhooks.length === 0 && (
        <EmptyState
          icon={
            <WebhookIcon aria-hidden="true" fontSize="large" color="disabled" />
          }
          title="No webhooks yet"
          description="Add a webhook to receive event notifications from pdfglyph."
          action={
            <Button variant="contained" onClick={() => setAddWebhookOpen(true)}>
              Add webhook
            </Button>
          }
        />
      )}

      {webhooks.map((webhook) => {
        const eventList = webhook.events
          ? webhook.events
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [];
        const maskedSecret =
          webhook.secret && webhook.secret.length > 12
            ? `${webhook.secret.slice(0, 12)}...`
            : webhook.secret;

        return (
          <Card key={webhook.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      maxWidth: "100%",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "monospace",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {webhook.url}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                  >
                    {eventList.map((evt) => (
                      <Chip
                        key={evt}
                        label={evt}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {maskedSecret}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Switch
                    checked={webhook.enabled}
                    size="small"
                    onChange={(_, checked) =>
                      handleToggleWebhook(webhook.id, checked)
                    }
                    inputProps={{
                      "aria-label": `Toggle webhook ${webhook.url}`,
                    }}
                  />
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      aria-label={`Delete webhook ${webhook.url}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {/* Add Endpoint dialog */}
      <Dialog
        open={addWebhookOpen}
        onClose={() => setAddWebhookOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Webhook Endpoint</DialogTitle>
        <DialogContent>
          <TextField
            label="URL"
            value={newWebhookUrl}
            onChange={(e) => setNewWebhookUrl(e.target.value)}
            fullWidth
            required
            placeholder="https://example.com/webhooks"
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Events
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newWebhookEventCompleted}
                  onChange={(_, checked) =>
                    setNewWebhookEventCompleted(checked)
                  }
                />
              }
              label="render.completed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newWebhookEventFailed}
                  onChange={(_, checked) => setNewWebhookEventFailed(checked)}
                />
              }
              label="render.failed"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWebhookOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddWebhook}
            disabled={!newWebhookUrl || addWebhookSaving}
          >
            {addWebhookSaving ? "Saving\u2026" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
